// The grenade form's board half (qa-runs#1171 part 3): the spot aimed at, picked with a tap on the board; the walls the
// DM marks broken for this blast; and, after the throw, the blast this person last threw (to draw the scatter from the
// aim to where it landed). The form and the overlay (BlastOverlays.tsx) share it; nothing here is sent anywhere.
// A thrown blast waits on the server until it is set off (blasts.ts): `held` says this form threw one, and the DM's
// `moveTo` is the spot they tapped to move it to first.
export interface BlastAimState {
  /** A tap on the board picks the spot. */
  aiming: boolean;
  /** A tap on a wall marks it broken, or whole again (the DM's). */
  marking: boolean;
  aimed: { x: number; y: number } | null;
  thrower: { x: number; y: number } | null;
  /** The reach drawn round the spot, in board pixels: where a Power of 1 still reaches if it lands true. */
  reachPx: number | null;
  broken: ReadonlySet<string>;
  /** After a throw: the area ids on the board before it, so the new blast is the one not among them. */
  thrownBefore: ReadonlySet<string> | null;
  open: boolean;
  /** This form threw a blast that has not gone off yet. */
  held: boolean;
  /** A tap on the board picks where the DM moves the held blast. */
  moving: boolean;
  moveTo: { x: number; y: number } | null;
}
const EMPTY: BlastAimState = { aiming: false, marking: false, aimed: null, thrower: null, reachPx: null, broken: new Set(), thrownBefore: null, open: false, held: false, moving: false, moveTo: null };
let state: BlastAimState = EMPTY;
const listeners = new Set<() => void>();
const set = (patch: Partial<BlastAimState>) => { state = { ...state, ...patch }; for (const l of listeners) l(); };

export const blastAim = {
  get: () => state,
  subscribe(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** The form opened (its overlay draws) or closed (all of it forgotten). */
  setOpen(open: boolean) { if (open) set({ open: true }); else { state = EMPTY; for (const l of listeners) l(); } },
  startAim() { set({ aiming: true, marking: false, moving: false }); },
  stopAim() { set({ aiming: false }); },
  pick(p: { x: number; y: number }) { set({ aimed: p, aiming: false, thrownBefore: null }); },
  setMarking(on: boolean) { set({ marking: on, aiming: false, moving: false }); },
  /** The DM's move of a held blast: the next tap picks the spot; `stopMove` cancels the tapping, `clearMove` the spot. */
  startMove() { set({ moving: true, aiming: false, marking: false }); },
  stopMove() { set({ moving: false }); },
  pickMove(p: { x: number; y: number }) { set({ moveTo: p, moving: false }); },
  clearMove() { set({ moveTo: null, moving: false }); },
  toggleWall(id: string) { const b = new Set(state.broken); if (b.has(id)) b.delete(id); else b.add(id); set({ broken: b }); },
  clearWalls() { set({ broken: new Set() }); },
  setPreview(thrower: { x: number; y: number } | null, reachPx: number | null) {
    if (state.thrower?.x === thrower?.x && state.thrower?.y === thrower?.y && state.reachPx === reachPx) return;
    set({ thrower, reachPx });
  },
  thrown(before: ReadonlySet<string>) { set({ thrownBefore: before, held: true, moveTo: null }); },
  /** The held blast went off (or there was none): nothing waits now. */
  wentOff() { set({ held: false, moving: false, moveTo: null }); },
};
